import { ApolloError } from '@apollo/client';
import { NavService } from '@thoughtspot/blink-context';
import { ContextMenuInputPoints } from '@thoughtspot/chart-common';
import { getIncompleteThreshold } from '@thoughtspot/chart-lib/lib/components/kpi/kpi-utils';
import { getXAxisColumns } from '@thoughtspot/chart-lib/lib/core/chart-model-utils';
import { isIncompleteDataFound } from '@thoughtspot/chart-lib/lib/data/data-util';
import {
    getEndEpoch,
    getStartEpoch,
} from '@thoughtspot/custom-calendar-service';
import { convertToSeconds } from '@thoughtspot/date-format-util';
import {
    FEATURE_FLAG_NAMES,
    isFeatureEnabled,
} from '@thoughtspot/feature-service';
import { translate, useTranslation } from '@thoughtspot/i18n';
import {
    MetricsService,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import {
    isKeyDriversAnalysisEnabled,
    isSpotIQV2Enabled,
    isWhatIfAnalysisEnabled,
} from '@thoughtspot/session-service';
import { hasCustomCalendar } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { FormEvent } from 'react';
import { useAppContext } from '/@contexts/global-app-context';
import { getUserSelectionPoints } from '/@services/answer/answer.util';
import {
    A3AnalysisConfig,
    A3AnalysisTypeEnum,
    A3Column,
    AnalysisAlgorithmTypeEnum,
    AnalysisClassEnum,
    AnalysisParam,
    AnalysisState,
    AnalysisType,
    AnswerColumn,
    ChartViz as ChartModel,
    ChartViz,
    ColumnType,
    CurrentDataOptionsEnum,
    MaxInsightOptions,
    SpotIqa3AnalysisConfigResponse,
    UserPointSelectionInput,
    Vendor,
    VendorConfig,
} from '/@services/generated/graphql-types';
import {
    isLiveboardEmbed,
    isSageEmbed,
    isSearchEmbed,
} from '/@utils/embed.util';
import { containsInApolloError } from '/@utils/error-handling/error-utils';
import { getTimeToExpireShort } from '../spotiq/analysis-list/spotiq-list-config';
import { getSpotIQMixPanelActionProps } from '../spotiq/spotiq-utils/spotiq-utils';
import {
    AnalysisStatus,
    getAnalysisVendorName,
    VendorCardParams,
} from '../spotiq-modal/spotiq-modal.util';

export const mockColumnData = [
    {
        column: 'Revenue',
        columnId: 'd8cd8978-bd01-4d57-910d-4781e8b33123',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Brand1',
        columnId: 'c56c023c-f3b0-43c6-8de2-ed8c25696542',
        table: 'PART',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Quantity',
        columnId: '8eab0b4b-e3ad-4114-a810-a3665c5defbd',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Tax',
        columnId: '6ac1cf06-71c5-4f08-a1b5-08033496ff17',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Discount',
        columnId: '98da48a9-670e-4d7e-b478-4c5d4218c01b',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Size',
        columnId: '0f64c03b-6eec-4e42-8a46-9589d8a3a5d6',
        table: 'PART',
        isSuggested: true,
        isSelected: true,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Part Name',
        columnId: 'd068bb59-4fd7-4e59-92d5-7f363b443ccb',
        table: 'PART',
        isSuggested: true,
        isSelected: true,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Year Month',
        columnId: 'ff8e61e0-bdb1-44c7-9ce3-2360f76dbfde',
        table: 'DATE',
        isSuggested: true,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Part PartKey',
        columnId: 'ae5e8fe0-ef00-4539-894a-c7267341e2d8',
        table: 'PART',
        isSuggested: true,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer City',
        columnId: '788e1538-6169-49a9-90b0-c1b116ec5ec2',
        table: 'CUSTOMER',
        isSuggested: true,
        isSelected: true,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Type',
        columnId: '6b2ac849-3146-4f41-b83b-2a0e6ef31f3e',
        table: 'PART',
        isSuggested: true,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Date',
        columnId: '85041b22-0337-4263-93bb-7b5b42be1fbe',
        table: 'DATE',
        isSuggested: true,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Color',
        columnId: '498c6665-c50b-4df7-9308-ea63f298df58',
        table: 'PART',
        isSuggested: true,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'OrderKey',
        columnId: '8dc397d5-a7ec-4d8a-ae7b-9a98faf04f93',
        table: 'LINEORDER',
        isSuggested: true,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Container',
        columnId: 'b7a08ebf-e27e-4753-9cd8-d54dc159cd1d',
        table: 'PART',
        isSuggested: true,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Year',
        columnId: 'f1f13666-15fa-48ed-bf9f-c6a3f42f25ea',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Month',
        columnId: 'b9ea456b-61ed-4db6-ac79-5ddd91278806',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Datekey',
        columnId: 'd6294dd8-3c18-48e7-a230-1d84c73a8a4b',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Industry',
        columnId: '310e2152-fcbb-4d48-a8ff-1decab626e5c',
        table: 'Part Details',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Category',
        columnId: 'ab1bf3da-1478-4786-b666-39ea9f930152',
        table: 'PART',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Ship Mode',
        columnId: '4f1e2f95-8ec6-4567-8c4c-bc77d55b9d67',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Holiday Fl',
        columnId: '8c2e1355-521f-4f8f-97ca-71de1ff2beb5',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Weekday Fl',
        columnId: '431c5d3f-4bf8-4eca-bf6e-9d08c94c60e3',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Order Date',
        columnId: 'fb029781-9300-4fa0-9ec8-ae2a4076d387',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Day Of Week',
        columnId: 'd6d76035-f63d-4fcf-bf96-5449d60d9e21',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Line Number',
        columnId: '3aa53aa7-d131-46ec-a487-c9a0f67fa6bb',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Commit Date',
        columnId: '7924554d-537e-4b85-b20e-01423626f5c3',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Manufacturer',
        columnId: '9f50ca56-5ef0-4246-9894-156b8d5694c9',
        table: 'PART',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Ship Priority',
        columnId: '94313c93-9ea7-4c67-b6ac-ee0d145922bd',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supplier City',
        columnId: '30767b8a-c789-40ec-9ac5-899239321889',
        table: 'SUPPLIER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supplier Name',
        columnId: '43ff35d7-bf89-4f18-b1d3-a56f38f470b8',
        table: 'SUPPLIER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer Name',
        columnId: '775665af-4b92-4e7c-952c-cde5a9fed181',
        table: 'CUSTOMER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer City',
        columnId: 'a1545928-1195-40b9-b3c4-78f837012afb',
        table: 'DiscountAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer City',
        columnId: 'bb4f0314-c44c-4dda-b85c-537120eaae9a',
        table: 'RevenueAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer City',
        columnId: '9617c673-3f93-42c5-81b6-2289071b0b6d',
        table: 'TaxAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Market Segment',
        columnId: '06c72a92-aded-4eca-84b6-38bc506bd92d',
        table: 'CUSTOMER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Order Priority',
        columnId: '94d39358-606d-4602-9fa6-136929b1ea69',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Selling Season',
        columnId: 'cfb144f7-e557-45f2-b995-cff58b855706',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supplier Phone',
        columnId: '36524a78-f347-44b4-ab5f-34519a78483e',
        table: 'SUPPLIER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer Phone',
        columnId: '20d83ff0-fe21-4be4-b9e3-d678dd389dc9',
        table: 'CUSTOMER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer Region',
        columnId: '8c1c4e39-b302-4e7f-80db-b19377c75df0',
        table: 'CUSTOMER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supplier Region',
        columnId: '0f7361ca-de08-43b8-8170-a759d92ec654',
        table: 'SUPPLIER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer Nation',
        columnId: 'aa3cd6c4-f04d-4d29-a951-1233273bd20f',
        table: 'CUSTOMER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supplier Nation',
        columnId: 'ddf4eb45-3698-4c1c-84cf-637ef21a3865',
        table: 'SUPPLIER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supplier Address',
        columnId: 'd01b1765-56e6-4b93-b7cc-9b308685ff64',
        table: 'SUPPLIER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supplier SuppKey',
        columnId: '022492f7-b6cb-4f0d-8980-2e6162897c03',
        table: 'SUPPLIER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer Address',
        columnId: 'fd2926e5-c390-4642-ac74-20ed5d378c94',
        table: 'CUSTOMER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Customer CustKey',
        columnId: 'da459aa6-5046-4afc-9604-bf75abfd8f14',
        table: 'CUSTOMER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Year Month Number',
        columnId: '5d3318cc-5183-433b-adec-ceff3304e06f',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Lineorder CustKey',
        columnId: 'a85876c3-61d9-48b2-a447-cd8c51c60709',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Lineorder Suppkey',
        columnId: 'dbe6f9f3-5dc2-4bb5-b6d6-2f2b91e9af80',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Lineorder PartKey',
        columnId: 'd1c6c143-beb1-4b2f-93a1-7ae773f12b9e',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Month(Order Date)',
        columnId: '82bc258e-07a6-404a-ab57-765341575cab',
        table: 'DiscountAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Day Number In Week',
        columnId: '83cab2f8-00db-4524-95c6-b47507d124f4',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Day Number In Year',
        columnId: '4050e0a1-d5d0-404d-957f-65f29ba019bd',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Month(Commit Date)',
        columnId: '0e42a7fc-6f70-48ee-acc5-9a8c5270d589',
        table: 'RevenueAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Last Day In Week Fl',
        columnId: '2ab2b3f1-d93b-49b8-8328-31f12c4cac14',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Day Number In Month',
        columnId: 'f9b99a0d-a204-49a2-9abe-24f5962312cc',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Week Number In Year',
        columnId: '814a36df-71c2-44b3-afc1-d483eea2b6ad',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Last Day In Month Fl',
        columnId: '6ba81bdf-9418-4947-9cd5-367e7f517e2b',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Month Number In Year',
        columnId: '2aaf162f-3568-4928-bca2-338e4a91c33a',
        table: 'DATE',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Total Tax',
        columnId: '31852722-e110-43ec-a0a9-88dd7a510424',
        table: 'TaxAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Supply Cost',
        columnId: 'e70b2306-98c2-4f86-8460-870fcb3712ee',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Part PartKey',
        columnId: 'ce70e999-5edb-4a8c-b751-0f33cc17724f',
        table: 'Part Details',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Total Revenue',
        columnId: 'e6521bef-4109-48f7-8b88-4121b0468927',
        table: 'RevenueAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Extended Price',
        columnId: 'f13886dd-2e38-4808-a47f-49da3a40bae6',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Total Discount',
        columnId: '0f3c9e3a-9c59-4881-b27a-d0e982b3ea8e',
        table: 'DiscountAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Order Total Price',
        columnId: '68b814a8-2389-498e-8dec-8ff0fae22809',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
];

export const mockedNotSelectedColumnData = [
    {
        column: 'Total Discount',
        columnId: '0f3c9e3a-9c59-4881-b27a-d0e982b3ea8e',
        table: 'DiscountAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Order Total Price',
        columnId: '68b814a8-2389-498e-8dec-8ff0fae22809',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Part PartKey',
        columnId: 'ce70e999-5edb-4a8c-b751-0f33cc17724f',
        table: 'Part Details',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Total Revenue',
        columnId: 'e6521bef-4109-48f7-8b88-4121b0468927',
        table: 'RevenueAggrWs',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
    {
        column: 'Extended Price',
        columnId: 'f13886dd-2e38-4808-a47f-49da3a40bae6',
        table: 'LINEORDER',
        isSuggested: false,
        isSelected: false,
        columnType: ColumnType.Attribute,
    },
];

export const mockAnalysisStatus = {
    state: AnalysisState.Running,
    progress: 0,
    analysisResultId: '',
    message: '',
};

export type PreviousAnalysisState = {
    selectedAnalysisClasses: AnalysisClassEnum[];
    selectedAlgorithmTypes: AnalysisAlgorithmTypeEnum[];
};

export enum SpotIQTestId {
    LOADING_INDICATOR_SPOTIQ_BOX = 'loading-indicator-spotIQ-box-container',
    LOADING_INDICATOR_GLOBAL_SPOTIQ_BOX = 'loading-indicator-global-spotIQ-box',
}

export type columnDataType = {
    column: string;
    columnId: string;
    table: string;
    isSelected: boolean;
    isSuggested: boolean;
    columnType: ColumnType;
};

export enum ModalView {
    SPLASH_SCREEN,
    BUSINESS_QUESTION,
    SELECT_COLUMNS,
    CUSTOMISE,
    ANALYSIS_BEGUN,
    EXPLAIN_CHANGE,
}

export interface SpotIQParams {
    minRows: number;
    multiplier: number;
    pValueThreshold: number;
    minRelativeDifference: number;
    corrCoeff: number;
    maxCorrCoeff: number;
    maxLag: number;
    maxDiffElements: number;
    maxFraction: number;
    minAbsChangeRatio: number;
    minChangeRatio: number;
    forecastedDatapoints: number;
    vendor: Vendor;
}

const NO_A3ANALYSIS_PRIVILEGE_CODE = ['10003'];

// **************************** Container Utils  ****************************

export const isNotAuthorized = (error: ApolloError) =>
    containsInApolloError(error, NO_A3ANALYSIS_PRIVILEGE_CODE);

export const getModalViewProps = (
    setCurrentModalView: (value: React.SetStateAction<ModalView>) => void,
    onSubmitAnalysis: () => void,
    isModalOpen: boolean,
    closeModal: () => void,
    navService: NavService,
    resetToDefault: () => void,
    noQuestionSelected: boolean,
    needsColumnSelection: boolean,
    metricsService: MetricsService,
    isUpdate?: boolean,
    a3AnalysisSubmissionLoading?: boolean,
    a3AnalysisColumnsDataError?: ApolloError,
    a3AnalysisColumnsDataLoading?: boolean,
    attributesLimitAlert?: string,
) => {
    const goToBusinessQuestionStep = () => {
        setCurrentModalView(ModalView.BUSINESS_QUESTION);
    };
    const goToSelectColumnsStep = () => {
        setCurrentModalView(ModalView.SELECT_COLUMNS);
    };
    const { t } = useTranslation();
    const { sessionService } = useAppContext();

    const hideGoToAnalysisBtn =
        isSageEmbed() || isSearchEmbed() || isLiveboardEmbed();
    const modalViewProps = new Map();
    const goToAnalysisPage = () => {
        const isModularHomeExperienceEnabled = sessionService.isModularHomeExperienceEnabled();
        metricsService.collectEvent(
            new UserAction(userActionTypeEnum.TAKE_ME_TO_ANALYSIS_BUTTON_CLICK),
            {},
        );
        // eslint-disable-next-line no-unused-expressions
        isModularHomeExperienceEnabled
            ? navService.goToHomeSpotIQAnalysis()
            : navService.goToInsights('analyses');
    };

    modalViewProps.set(ModalView.SPLASH_SCREEN, {
        isOpen: isModalOpen,
        title: '',
        confirmButton: {
            text: t('a3.triggerModal.splashButtonText'),
            onConfirm: goToBusinessQuestionStep,
        },
        includesFooter: false,
        includesHeader: false,
        onDismiss: closeModal,
        hasCrossButton: true,
        dismissButton: null,
        alwaysMaxHeight: true,
    });

    modalViewProps.set(ModalView.BUSINESS_QUESTION, {
        isOpen: isModalOpen,
        title: isUpdate ? t('UPDATE') : t('a3.spotiqAnalysis'),
        confirmButton: {
            text: needsColumnSelection
                ? t('a3.continue')
                : t('a3.spotiqAnalysis'),
            onConfirm: needsColumnSelection
                ? goToSelectColumnsStep
                : onSubmitAnalysis,
            isDisabled: noQuestionSelected,
        },
        hasCrossButton: true,
        onDismiss: closeModal,
        alwaysMaxHeight: true,
    });

    modalViewProps.set(ModalView.SELECT_COLUMNS, {
        isOpen: isModalOpen,
        title: t('a3.spotiqAnalysis'),
        confirmButton: {
            text: t('a3.triggerAnalysis'),
            onConfirm: onSubmitAnalysis,
            isDisabled:
                !!a3AnalysisSubmissionLoading ||
                !!attributesLimitAlert ||
                !!a3AnalysisColumnsDataLoading ||
                isNotAuthorized(a3AnalysisColumnsDataError),
            isLoading: !!a3AnalysisSubmissionLoading,
        },
        hasCrossButton: true,
        dismissButton: {
            text: t('Back'),
            onClick: goToBusinessQuestionStep,
        },
        onDismiss: closeModal,
        alwaysMaxHeight: true,
    });

    modalViewProps.set(ModalView.EXPLAIN_CHANGE, {
        isOpen: isModalOpen,
        title: t('a3.spotiqAnalysis'),
        confirmButton: {
            text: t('a3.triggerAnalysis'),
            onConfirm: onSubmitAnalysis,
        },
        hasCrossButton: false,
        dismissButton: {
            text: t('modal.cancel'),
        },
        onDismiss: closeModal,
        alwaysMaxHeight: true,
    });

    modalViewProps.set(ModalView.CUSTOMISE, {
        isOpen: isModalOpen,
        title: t('a3.spotiqAnalysis'),
        confirmButton: {
            text: t('a3.triggerAnalysis'),
            onConfirm: onSubmitAnalysis,
            isDisabled: isNotAuthorized(a3AnalysisColumnsDataError),
        },
        dismissButton: {
            text: t('modal.cancel'),
        },
        denyButton: {
            text: t('a3.reset'),
            onDeny: resetToDefault,
        },
        hasCrossButton: true,
        onDismiss: closeModal,
        alwaysMaxHeight: true,
    });

    modalViewProps.set(ModalView.ANALYSIS_BEGUN, {
        isOpen: isModalOpen,
        title: t('a3.spotiqAnalysis'),
        dismissButton: {
            text: t('Done'),
            onClick: closeModal,
        },
        confirmButton: !hideGoToAnalysisBtn
            ? {
                  text: t('a3.goToAnalysisPage'),
                  onConfirm: goToAnalysisPage,
              }
            : null,
        hasCrossButton: true,
        onDismiss: closeModal,
        alwaysMaxHeight: true,
    });
    return modalViewProps;
};

export const goToSpotIQInsight = (
    navService: NavService,
    spotIQAnalysisStatus: AnalysisStatus,
    spotIQMixPanelActionProps?: any,
    metricsService?: MetricsService,
) => {
    metricsService.collectEvent(
        new UserAction(userActionTypeEnum.OPEN_SPOTIQ_INSIGHT_PINBOARD),
        getSpotIQMixPanelActionProps({
            analysisResultId: spotIQAnalysisStatus?.analysisResultId,
            resultStatus: spotIQAnalysisStatus?.state,
            expiryStatus: getTimeToExpireShort(
                spotIQAnalysisStatus?.expiryDeadline,
            ),
            ...spotIQMixPanelActionProps,
            newSpotIQModal: isSpotIQV2Enabled(),
        }),
    );
    if (spotIQAnalysisStatus?.analysisResultId)
        navService.goToInsight(spotIQAnalysisStatus?.analysisResultId);
};

export const checkSelectedColumnsLimit = (
    columnDataState: columnDataType[],
    insightCountSettingsState: any,
    setAttributesLimitAlert: (attributesLimitAlert: string) => void,
) => {
    const {
        selectedAttributeColumnsCount,
        selectedMeasureColumnsCount,
    } = columnDataState?.reduce(
        (counts, column) => {
            const updatedCounts = { ...counts };

            if (column.isSelected) {
                if (column.columnType === ColumnType.Attribute) {
                    updatedCounts.selectedAttributeColumnsCount++;
                } else if (column.columnType === ColumnType.Measure) {
                    updatedCounts.selectedMeasureColumnsCount++;
                }
            }
            return updatedCounts;
        },
        { selectedAttributeColumnsCount: 0, selectedMeasureColumnsCount: 0 },
    );

    if (
        selectedAttributeColumnsCount >
            insightCountSettingsState.maxDrillAttributes &&
        selectedMeasureColumnsCount > insightCountSettingsState.maxMeasures
    )
        setAttributesLimitAlert(
            translate(
                'CUSTOMIZE_MEASURES_AND_ATTRIBUTES_MAX_LIMIT_ERROR_TEXT_DYNAMIC',
                {
                    max_attributes:
                        insightCountSettingsState.maxDrillAttributes,
                    max_measures: insightCountSettingsState.maxMeasures,
                },
            ),
        );
    else if (
        selectedAttributeColumnsCount >
        insightCountSettingsState.maxDrillAttributes
    )
        setAttributesLimitAlert(
            translate('CUSTOMIZE_ATTRIBUTES_MAX_LIMIT_ERROR_TEXT_DYNAMIC', {
                max_attributes: insightCountSettingsState.maxDrillAttributes,
            }),
        );
    else if (
        selectedMeasureColumnsCount > insightCountSettingsState.maxMeasures
    )
        setAttributesLimitAlert(
            translate('CUSTOMIZE_MEASURES_MAX_LIMIT_ERROR_TEXT_DYNAMIC', {
                max_measures: insightCountSettingsState.maxMeasures,
            }),
        );
    else setAttributesLimitAlert('');
};
// ***************************** Static/default values  *****************************

const spotIQQuestions = () => [
    translate('a3.OUTLIER_ANALYSIS'),
    translate('a3.TREND_ANALYSIS'),
    translate('a3.CROSS_CORRELATION'),
    translate('a3.CHANGE_ANALYSIS'),
];

export const DEFAULT_FORECAST_MIN_ROWS = 15;

export const getForecastQuestionDetails = (
    minSupportedRows = DEFAULT_FORECAST_MIN_ROWS,
) => {
    const isWhatIfEnabled = isWhatIfAnalysisEnabled();

    return {
        title: isWhatIfEnabled
            ? translate('a3.title.FORECAST_WHAT_IF')
            : translate('a3.title.FORECAST'),
        question: isWhatIfEnabled
            ? translate('a3.question.FORECAST_WHAT_IF')
            : translate('a3.question.FORECAST'),
        disabledTooltip: isWhatIfEnabled
            ? translate('a3.question.disabledTooltip.FORECAST_WHAT_IF', {
                  points: minSupportedRows,
              })
            : translate('a3.question.disabledTooltip.FORECAST', {
                  points: minSupportedRows,
              }),
    };
};

export const getQuestionsNeedingColumnSelection = () => {
    const questions = [...spotIQQuestions()];
    if (isSpotIQV2Enabled()) {
        questions.push(getForecastQuestionDetails().title);
    }

    return questions;
};

/**
 * @returns This method returns the list of questions which needs analysis settings to be configured
 */
export const getQuestionsNeedingAnalysisSettings = () => {
    const questions = [...spotIQQuestions()];
    if (isSpotIQV2Enabled()) {
        questions.push(getForecastQuestionDetails().title);
    }

    return questions;
};

export const getBusinessQuestionList = () => {
    const questions = [...spotIQQuestions()];
    if (isSpotIQV2Enabled()) {
        questions.push(getForecastQuestionDetails().title);
        if (isKeyDriversAnalysisEnabled()) {
            questions.push(translate('a3.title.KEY_DRIVERS'));
        }
    }

    return questions;
};

export const handleChangeInsightCount = (
    key: string,
    value: number,
    insightCountSettingsState: any,
    setInsightCountSettingsState: (insightCountSettingsState: any) => void,
) => {
    const newState = _.cloneDeep(insightCountSettingsState);
    newState[key] = value;
    setInsightCountSettingsState(newState);
};

export const handleChangeAlgorithm = (
    algorithmTitle: string,
    isSelected: boolean,
    questionListState: A3BusinessQuestionConfig[],
    setQuestionListState: (
        questionListState: A3BusinessQuestionConfig[],
    ) => void,
) => {
    setQuestionListState(
        questionListState.map((ques: any) => {
            return {
                ...ques,
                algorithms: ques.algorithms.map((algo: any) => {
                    if (algo.name === algorithmTitle) {
                        const updatedAlgorithm = {
                            ...algo,
                            isSelected,
                        };

                        // If algo.name is "outlier detection" and isSelected is false,
                        // update algo.subparts[0].isSelected and algo.subparts[1].isSelected
                        if (algo.subparts) {
                            if (!isSelected) {
                                updatedAlgorithm.subparts = (
                                    algo.subparts || []
                                ).map((subpart: any) => ({
                                    ...subpart,
                                    isSelected,
                                }));
                            } else {
                                updatedAlgorithm.subparts[0].isSelected = isSelected;
                            }
                        }
                        return updatedAlgorithm;
                    }

                    return algo;
                }),
            };
        }),
    );
};

export const handleChangeParam = (
    key: string,
    value: number,
    paramState: SpotIQParams,
    setParamState: (paramState: SpotIQParams) => void,
) => {
    const newState: any = _.cloneDeep(paramState);
    newState[key] = value;
    setParamState(newState);
};

export const handleChangeGeneralSettings = (
    settingTitle: string,
    isSelected: boolean,
    generalSettingsState: any,
    setGeneralSettingsState: (generalSettingsState: any) => void,
) => {
    setGeneralSettingsState(
        generalSettingsState.map((setting: any) =>
            setting.title === settingTitle
                ? { ...setting, isSelected }
                : setting,
        ),
    );
};

export const handleChangeRadioButton = (
    event: FormEvent<HTMLFieldSetElement>,
    question: A3BusinessQuestionConfig,
    questionListState: A3BusinessQuestionConfig[],
    setQuestionListState: (
        questionListState: A3BusinessQuestionConfig[],
    ) => void,
) => {
    const value = _.get(event, 'target.value');
    const updatedQuestion = question;
    if (value === 'median') {
        updatedQuestion.algorithms[0].subparts[0].isSelected = true;
        updatedQuestion.algorithms[0].subparts[1].isSelected = false;
    } else {
        updatedQuestion.algorithms[0].subparts[0].isSelected = false;
        updatedQuestion.algorithms[0].subparts[1].isSelected = true;
    }

    const updatedQuestionListState = questionListState.map(question =>
        question.type === updatedQuestion.type ? updatedQuestion : question,
    );

    setQuestionListState(updatedQuestionListState);
};

export const getAlgorithmList = () => [
    translate('a3.Z_SCORE_OUTLIER_DETECTION'),
    translate('a3.MEDIAN_Z_SCORE_OUTLIER_DETECTION'),
    translate('a3.SHESD_OUTLIER_DETECTION'),
    translate('a3.LINEAR_REGRESSION_OUTLIER_DETECTION'),
    translate('a3.TREND_ANALYSIS'),
    translate('a3.CROSS_CORRELATION'),
    translate('a3.CHANGE_ANALYSIS'),
];
export const getParamList = () => [
    translate('a3.Min_Rows'),
    translate('a3.Multiplier'),
    translate('a3.P_Value_Threshold'),
    translate('a3.Correlation_Coefficient'),
    translate('a3.Max_Correlation_Coefficient'),
    translate('a3.Max_Lag'),
    translate('a3.Min_Relative_Difference'),
    translate('a3.Max_Diff_Elements'),
    translate('a3.Max_Fraction'),
    translate('a3.Min_Abs_Change_Ratio'),
    translate('a3.Min_Change_Ratio'),
];
export const getInsightCountList = () => [
    translate('a3.Max_Measures'),
    translate('a3.Max_Attributes'),
    translate('a3.Max_Anomaly_Insights'),
    translate('a3.Max_Seasonality_Detection_Anomaly_Insights'),
    translate('a3.Max_Linear_Regression_Anomaly_Insights'),
    translate('a3.Max_Trend_Insights'),
    translate('a3.Max_Cross_Correlation_Insights'),
];
export const getAnalysisTitle = (analysisType: AnalysisType) => {
    switch (analysisType) {
        case AnalysisType.Forecast:
            return translate('a3.title.FORECAST');

        case AnalysisType.KeyDrivers:
            return translate('a3.title.KEY_DRIVERS');

        case AnalysisType.WhatIf:
            return translate('a3.title.FORECAST_WHAT_IF');

        default:
            return ''; // Default value if analysis type is not recognized
    }
};
export const getInsightMap = () => {
    const insightMap = new Map();
    insightMap.set(getInsightCountList()[0], 'maxMeasures');
    insightMap.set(getInsightCountList()[1], 'maxDrillAttributes');
    insightMap.set(getInsightCountList()[2], 'maxAnomalies');
    insightMap.set(getInsightCountList()[3], 'maxShesdAnomalies');
    insightMap.set(getInsightCountList()[4], 'maxLrAnomalies');
    insightMap.set(getInsightCountList()[5], 'maxTrends');
    insightMap.set(getInsightCountList()[6], 'maxCrossCorrelationInsights');
    return insightMap;
};

export const getParamMap = (): Map<string, keyof SpotIQParams> => {
    const paramMap: Map<string, keyof SpotIQParams> = new Map();
    paramMap.set(getParamList()[0], 'minRows');
    paramMap.set(getParamList()[1], 'multiplier');
    paramMap.set(getParamList()[2], 'pValueThreshold');
    paramMap.set(getParamList()[3], 'corrCoeff');
    paramMap.set(getParamList()[4], 'maxCorrCoeff');
    paramMap.set(getParamList()[5], 'maxLag');
    paramMap.set(getParamList()[6], 'minRelativeDifference');
    paramMap.set(getParamList()[7], 'maxDiffElements');
    paramMap.set(getParamList()[8], 'maxFraction');
    paramMap.set(getParamList()[9], 'minAbsChangeRatio');
    paramMap.set(getParamList()[10], 'minChangeRatio');
    return paramMap;
};

export const paramsProps: Record<string, any> = Object.freeze({
    minRows: {
        min: 0,
        max: Number.MAX_SAFE_INTEGER,
        step: 1,
        precision: 0,
    },
    multiplier: {
        min: 0,
        max: Number.MAX_SAFE_INTEGER,
        step: 1,
        precision: undefined,
    },
    pValueThreshold: {
        min: 0,
        max: 1,
        step: 0.1,
        precision: undefined,
    },
    corrCoeff: {
        min: -1,
        max: 1,
        step: 0.1,
        precision: undefined,
    },
    maxCorrCoeff: {
        min: -1,
        max: 1,
        step: 0.1,
        precision: undefined,
    },
    maxLag: {
        min: Number.MIN_SAFE_INTEGER,
        max: Number.MAX_SAFE_INTEGER,
        step: 1,
        precision: undefined,
    },
    minRelativeDifference: {
        min: 0,
        max: Number.MAX_SAFE_INTEGER,
        step: 1,
        precision: undefined,
    },
});

export const getQuestionMap = () => {
    const questionMap = new Map();
    questionMap.set('general', {
        parameters: [],
        insightCountSettings: [
            getInsightCountList()[0],
            getInsightCountList()[1],
        ],
    });
    questionMap.set(getBusinessQuestionList()[0], {
        parameters: [getParamList()[0], getParamList()[1], getParamList()[2]],
        insightCountSettings: [
            getInsightCountList()[2],
            getInsightCountList()[3],
            getInsightCountList()[4],
        ],
    });
    questionMap.set(getBusinessQuestionList()[1], {
        parameters: [getParamList()[0], getParamList()[2], getParamList()[6]],
        insightCountSettings: [getInsightCountList()[5]],
    });
    questionMap.set(getBusinessQuestionList()[2], {
        parameters: [
            getParamList()[0],
            getParamList()[3],
            getParamList()[4],
            getParamList()[5],
        ],
        insightCountSettings: [getInsightCountList()[6]],
    });
    questionMap.set(getBusinessQuestionList()[3], {
        parameters: [
            getParamList()[7],
            getParamList()[8],
            getParamList()[9],
            getParamList()[10],
        ],
        insightCountSettings: [],
    });
    return questionMap;
};

export enum A3AlgorithmType {
    OutlierDetectionZScores,
    OutlierDetectionMedianZScores,
    OutlierDetectionSHESD,
    OutlierDetectionLinearRegression,
    TrendAnalysis,
    CrossCorrelationAnalysis,
    ChangeAnalysis,
    KeyDrivers,
    Forecast,
    WhatIf,
}

export enum A3AnalysisQuestionType {
    Outlier = 'Outlier',
    Trend = 'Trend',
    CrossCorrelation = 'CrossCorrelation',
    ChangeAnalysis = 'ChangeAnalysis',
    Forecast = 'Forecast',
    KeyDrivers = 'KeyDrivers',
}

export const AnalysisIds = {
    FORECAST_ID: 'forecast_what_if',
    KEY_DRIVERS_ID: 'key_drivers',
    OUTLIER_ID: 'outlier',
    TREND_ID: 'trend',
    CROSS_CORRELATION_ID: 'cross_correlation',
    CHANGE_ANALYSIS_ID: 'change_analysis',
};

/**
 * Config to represent SpotIQ questions and their UI state
 */
export type A3BusinessQuestionConfig = {
    id: string;
    title: string;
    type: A3AnalysisQuestionType;
    analysisType?: AnalysisType;
    shortName: string;
    question: string;
    image: string;
    isSelected: boolean;
    disabledTooltip?: string;
    algorithms: {
        name: string;
        isSelected: boolean;
        type?: A3AlgorithmType;
        subparts?: {
            name: string;
            type: A3AlgorithmType;
            isSelected: boolean;
        }[];
    }[];
};

export const getConfigForAnalysisType = (
    a3AnalysisConfig: SpotIqa3AnalysisConfigResponse,
    analysisType: AnalysisType,
): A3AnalysisConfig => {
    if (!analysisType) {
        return null;
    }

    return _.find(
        a3AnalysisConfig?.analysisConfig,
        config => config?.analysisType === analysisType,
    );
};

/**
 * Fetch vendorInfo for a particular analysis from the a3AnalysisConfig
 * @param a3AnalysisConfig a3AnalysisConfig object
 * @param analysisType analysisType e.g. Forecast
 * @param vendor vendor e.g. ThoughtSpot
 * @returns vendor info or list of all vendors
 */
export const getVendorInfoForAnalysisType = (
    a3AnalysisConfig: SpotIqa3AnalysisConfigResponse,
    analysisType: AnalysisType,
    vendor: Vendor,
): VendorConfig => {
    const analysisConfig = getConfigForAnalysisType(
        a3AnalysisConfig,
        analysisType,
    );

    return _.find(
        analysisConfig?.vendorConfig,
        config => config?.vendor === vendor,
    );
};

export const getMinRowsOfAllVendorsForAnalysisType = (
    a3AnalysisConfig: SpotIqa3AnalysisConfigResponse,
    analysisType: AnalysisType,
): number => {
    const analysisConfig = getConfigForAnalysisType(
        a3AnalysisConfig,
        analysisType,
    );
    const minRows = (analysisConfig?.vendorConfig || []).reduce(
        (min, config) => {
            const minRows = config?.minRows;
            return minRows !== undefined && minRows < min ? minRows : min;
        },
        Infinity,
    );

    const finalMinRows =
        minRows !== Infinity ? minRows : DEFAULT_FORECAST_MIN_ROWS;
    return finalMinRows;
};

export const getNumChartPointsFromChartViz = (chartViz: ChartModel): number => {
    const xAxisColumn = _.first(getXAxisColumns(chartViz));
    const columnDataLite = _.get(chartViz, 'data.0.columnDataLite');
    const xAxisColumnData =
        columnDataLite?.find(
            (colData: any) => colData.columnId === (xAxisColumn || {}).id,
        ) ?? {};

    const numChartPoints: number = xAxisColumnData?.dataValue?.length;
    return numChartPoints;
};

export const getAnalysisVendorDisabledToolTip = (
    minSupportedRows: number,
    analysisType: AnalysisType,
    vendor: Vendor,
) => {
    const vendorName = getAnalysisVendorName(vendor);
    return translate('a3.question.disabledTooltip.vendor', {
        points: minSupportedRows,
        analysisType: getAnalysisTitle(analysisType),
        vendor: vendorName,
    });
};

export const isAnalysisVendorDisabled = (
    numChartPoints: number,
    a3AnalysisConfig: SpotIqa3AnalysisConfigResponse,
    analysisType: AnalysisType,
    vendor: Vendor,
): string => {
    const vendorConfig: VendorConfig = getVendorInfoForAnalysisType(
        a3AnalysisConfig,
        analysisType,
        vendor,
    );

    const minSupportedRows: number =
        vendorConfig?.minRows || DEFAULT_FORECAST_MIN_ROWS;

    const disabledTooltip = getAnalysisVendorDisabledToolTip(
        minSupportedRows,
        analysisType,
        vendor,
    );

    // If number of data points in the chart is less than the minimum supported rows
    return numChartPoints < minSupportedRows ? disabledTooltip : '';
};

export const getSupportedAnalysisVendors = (
    a3AnalysisConfig: SpotIqa3AnalysisConfigResponse,
    analysisType: AnalysisType,
    numChartPoints: number,
): VendorCardParams[] => {
    const analysisConfig = getConfigForAnalysisType(
        a3AnalysisConfig,
        analysisType,
    );

    return analysisConfig?.vendorConfig?.map(config => {
        return {
            vendor: config.vendor,
            disabledTooltip: isAnalysisVendorDisabled(
                numChartPoints,
                a3AnalysisConfig,
                analysisType,
                config.vendor,
            ),
        };
    });
};

export const supportedCustomCalendar = (chartViz: ChartModel) => {
    const xAxisColumn = _.first(getXAxisColumns(chartViz));
    if (xAxisColumn && hasCustomCalendar(xAxisColumn)) {
        const columnDataLite = _.get(chartViz, 'data.0.columnDataLite');
        const xAxisColumnData = columnDataLite?.find(
            (colData: any) => colData.columnId === (xAxisColumn || {}).id,
        );
        const firstPoint = xAxisColumnData?.dataValue[0];

        if (firstPoint?.d || firstPoint?.v?.e) {
            return translate(
                'a3.question.disabledTooltip.FORECAST_CUSTOM_CALENDAR',
            );
        }
    }
    return '';
};

/**
 * Filters incomplete data from chart model and returns a new filtered data structure.
 * This function does not modify the original chartModel but returns filtered data.
 *
 * @param {ChartModel} chartModel - The chart model containing the data series.
 * @param {boolean} isFromEnd - Whether to filter from end or start.
 * @param {boolean} hasCustomCalendar - Whether the data has custom calendar.
 * @param {number} incompleteThresholdInSeconds - The threshold for incomplete data in seconds.
 * @returns {Array|null} - The filtered data array or null if no valid data exists.
 */
export const filterIncompleteDataIfPresent = (
    chartModel: ChartModel,
    isFromEnd = true,
    hasCustomCalendar = false,
    incompleteThresholdInSeconds = 0,
) => {
    const chartData = chartModel?.data?.[0]?.columnDataLite;

    // Ensure x and y data arrays exist and are aligned
    if (
        !chartData ||
        !chartData[0] ||
        !chartData[1] ||
        chartData[0].dataValue.length !== chartData[1].dataValue.length
    ) {
        return null;
    }

    const xAxis = [...chartData[0].dataValue]; // Create copy
    const yAxis = [...chartData[1].dataValue]; // Create copy

    if (!isFromEnd) {
        // Filter any incomplete dates at the start
        const validStartIndex = xAxis.findIndex((val: any) =>
            isIncompleteDataFound(
                hasCustomCalendar,
                incompleteThresholdInSeconds,
                val,
            ),
        );

        if (validStartIndex > 0) {
            return [
                {
                    ...chartData[0],
                    dataValue: xAxis.slice(validStartIndex),
                },
                {
                    ...chartData[1],
                    dataValue: yAxis.slice(validStartIndex),
                },
            ];
        }
    } else {
        // Filter any incomplete dates at the end
        const reversedIndices = xAxis.map(
            (_, index) => xAxis.length - 1 - index,
        );
        const validEndIndexFromReverse = reversedIndices.find(index =>
            isIncompleteDataFound(
                hasCustomCalendar,
                incompleteThresholdInSeconds,
                xAxis[index],
            ),
        );
        const validEndIndex =
            validEndIndexFromReverse !== undefined
                ? validEndIndexFromReverse
                : -1;

        if (validEndIndex >= 0 && validEndIndex < xAxis.length - 1) {
            return [
                {
                    ...chartData[0],
                    dataValue: xAxis.slice(0, validEndIndex + 1),
                },
                {
                    ...chartData[1],
                    dataValue: yAxis.slice(0, validEndIndex + 1),
                },
            ];
        }
        if (validEndIndex === -1) {
            return [
                {
                    ...chartData[0],
                    dataValue: [],
                },
                {
                    ...chartData[1],
                    dataValue: [],
                },
            ];
        }
    }

    // Return original data structure if no filtering needed
    return [
        {
            ...chartData[0],
            dataValue: xAxis,
        },
        {
            ...chartData[1],
            dataValue: yAxis,
        },
    ];
};

/**
 * Filters incomplete data and returns the count of chart points after filtering.
 * This function handles the complete workflow of filtering incomplete data for forecast analysis.
 *
 * @param chartViz - The chart visualization model
 * @param xAxisColumn - The X-axis column from the chart
 * @param originalChartPointsCount - The original number of chart points before filtering
 * @returns The number of chart points after filtering incomplete data
 */
export const getFilteredChartPointsCount = (
    chartViz: ChartModel,
    xAxisColumn: AnswerColumn,
    originalChartPointsCount: number,
): number => {
    // Get sort information from chart visualization
    const sortInfo = chartViz?.sortOrder?.length
        ? chartViz?.sortOrder
        : chartViz?.sortInfo;

    // Determine if X-axis is sorted in ascending order
    const isXAxisSortAsc =
        sortInfo?.find(_ => _.columnId === xAxisColumn.id)?.sortAscending ??
        true;

    // Get incomplete threshold and convert to seconds
    const incompleteThreshold = getIncompleteThreshold(chartViz);
    const incompleteThresholdInSeconds = convertToSeconds(incompleteThreshold);

    // Check if the chart uses custom calendar data
    const hasCustomCalendarData = xAxisColumn && hasCustomCalendar(xAxisColumn);

    // Filter incomplete data and get the filtered chart model
    const filteredChartModel = filterIncompleteDataIfPresent(
        chartViz,
        isXAxisSortAsc, // isFromEnd
        !!hasCustomCalendarData,
        incompleteThresholdInSeconds,
    );

    // Extract the filtered chart points count
    return _.get(
        filteredChartModel,
        '[0].dataValue.length',
        originalChartPointsCount,
    );
};

/**
 * Method to check whether analysis is disabled for a chart
 * @returns reason for analysis disabled
 */
export const isA3AnalysisDisabled = (
    chartViz: ChartModel,
    a3AnalysisConfig: SpotIqa3AnalysisConfigResponse,
    analysisType: AnalysisType,
): string => {
    if (!a3AnalysisConfig || !chartViz) {
        return '';
    }

    switch (analysisType) {
        case AnalysisType.Forecast: {
            const minSupportedRows = getMinRowsOfAllVendorsForAnalysisType(
                a3AnalysisConfig,
                analysisType,
            );
            const customCalendarValidStatus = supportedCustomCalendar(chartViz);
            if (customCalendarValidStatus) {
                return customCalendarValidStatus;
            }

            const xAxisColumn = _.first(getXAxisColumns(chartViz));
            const columnDataLite = _.get(chartViz, 'data.0.columnDataLite');
            const xAxisColumnData =
                columnDataLite?.find(
                    (colData: any) =>
                        colData.columnId === (xAxisColumn || {}).id,
                ) ?? {};

            let numChartPoints: number = xAxisColumnData?.dataValue?.length;
            if (numChartPoints >= minSupportedRows) {
                numChartPoints = getFilteredChartPointsCount(
                    chartViz,
                    xAxisColumn,
                    numChartPoints,
                );
            }
            const disabledTooltip = getForecastQuestionDetails(minSupportedRows)
                .disabledTooltip;

            // If number of data points in the chart is less than the minimum supported rows
            return numChartPoints < minSupportedRows ? disabledTooltip : '';
        }
        default:
            return '';
    }
};

export const getSelectedQuestionFromQuestionListState = (
    questionListState: A3BusinessQuestionConfig[],
): A3BusinessQuestionConfig => {
    return _.find(
        questionListState,
        (question: A3BusinessQuestionConfig) => question.isSelected,
    );
};

export const getQuestionList = (
    isRerunAnalysis: boolean,
    previousAnalysisState?: PreviousAnalysisState,
    isKPITimeseries?: boolean,
    analysisType?: A3AnalysisTypeEnum,
    chartViz?: ChartModel,
    a3AnalysisConfig?: SpotIqa3AnalysisConfigResponse,
): A3BusinessQuestionConfig[] => {
    let selectedAnalysisClasses: AnalysisClassEnum[];
    let selectedAlgorithmTypes: AnalysisAlgorithmTypeEnum[];
    if (isRerunAnalysis) {
        ({
            selectedAnalysisClasses,
            selectedAlgorithmTypes,
        } = previousAnalysisState);
    }

    const isQuestionPreselected = {
        [AnalysisClassEnum.Forecast]: isRerunAnalysis
            ? selectedAnalysisClasses.includes(AnalysisClassEnum.Forecast)
            : isSpotIQV2Enabled(),
        [AnalysisClassEnum.KeyDrivers]: isRerunAnalysis
            ? selectedAnalysisClasses.includes(AnalysisClassEnum.KeyDrivers)
            : false,
        [AnalysisClassEnum.OutlierDetection]: isRerunAnalysis
            ? selectedAnalysisClasses.includes(
                  AnalysisClassEnum.OutlierDetection,
              )
            : !isSpotIQV2Enabled(),
        [AnalysisClassEnum.TrendAnalysis]: isRerunAnalysis
            ? selectedAnalysisClasses.includes(AnalysisClassEnum.TrendAnalysis)
            : !isSpotIQV2Enabled(),
        [AnalysisClassEnum.CrossCorrelation]: isRerunAnalysis
            ? selectedAnalysisClasses.includes(
                  AnalysisClassEnum.CrossCorrelation,
              )
            : !isSpotIQV2Enabled(),
    };
    const isAlgorithmPreselected = {
        [AnalysisAlgorithmTypeEnum.MadMedian]:
            isRerunAnalysis &&
            isQuestionPreselected[AnalysisClassEnum.OutlierDetection]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.MadMedian,
                  )
                : true,
        [AnalysisAlgorithmTypeEnum.StdevMean]:
            isRerunAnalysis &&
            isQuestionPreselected[AnalysisClassEnum.OutlierDetection]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.StdevMean,
                  )
                : false,
        [AnalysisAlgorithmTypeEnum.Shesd]:
            isRerunAnalysis &&
            isQuestionPreselected[AnalysisClassEnum.OutlierDetection]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.Shesd,
                  )
                : false,
        [AnalysisAlgorithmTypeEnum.LinearRegression]:
            isRerunAnalysis &&
            isQuestionPreselected[AnalysisClassEnum.OutlierDetection]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.LinearRegression,
                  )
                : true,
        [AnalysisAlgorithmTypeEnum.TrendAnalysis]:
            isRerunAnalysis &&
            isQuestionPreselected[AnalysisClassEnum.TrendAnalysis]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.TrendAnalysis,
                  )
                : true,
        [AnalysisAlgorithmTypeEnum.CrossCorrelation]:
            isRerunAnalysis &&
            isQuestionPreselected[AnalysisClassEnum.CrossCorrelation]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.CrossCorrelation,
                  )
                : true,
        [AnalysisAlgorithmTypeEnum.Forecast]:
            isRerunAnalysis && isQuestionPreselected[AnalysisClassEnum.Forecast]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.Forecast,
                  )
                : true,
        [AnalysisAlgorithmTypeEnum.KeyDrivers]:
            isRerunAnalysis &&
            isQuestionPreselected[AnalysisClassEnum.KeyDrivers]
                ? selectedAlgorithmTypes.includes(
                      AnalysisAlgorithmTypeEnum.KeyDrivers,
                  )
                : true,
    };

    const questionList: A3BusinessQuestionConfig[] = [];

    if (
        isSpotIQV2Enabled() &&
        isKPITimeseries &&
        analysisType === A3AnalysisTypeEnum.Visualization
    ) {
        const disabledTooltip = isA3AnalysisDisabled(
            chartViz,
            a3AnalysisConfig,
            AnalysisType.Forecast,
        );
        const forecastQuestion = getForecastQuestionDetails().question;
        questionList.push({
            id: AnalysisIds.FORECAST_ID,
            title: getBusinessQuestionList()[4],
            type: A3AnalysisQuestionType.Forecast,
            analysisType: AnalysisType.Forecast,
            shortName: forecastQuestion,
            question: forecastQuestion,
            image: 'resources/img/spotiq-img/Forecasting.svg',
            disabledTooltip,
            isSelected: !disabledTooltip,
            algorithms: [
                {
                    name: forecastQuestion,
                    type: A3AlgorithmType.Forecast,
                    isSelected: !disabledTooltip,
                },
            ],
        });

        if (isKeyDriversAnalysisEnabled()) {
            questionList.push({
                id: AnalysisIds.KEY_DRIVERS_ID,
                title: getBusinessQuestionList()[5],
                type: A3AnalysisQuestionType.KeyDrivers,
                analysisType: AnalysisType.KeyDrivers,
                shortName: translate('a3.question.KEY_DRIVERS'),
                question: translate('a3.question.KEY_DRIVERS'),
                image: 'resources/img/spotiq-img/KeyDrivers.svg',
                isSelected: isQuestionPreselected[AnalysisClassEnum.KeyDrivers],
                algorithms: [
                    {
                        name: translate('a3.question.KEY_DRIVERS'),
                        type: A3AlgorithmType.KeyDrivers,
                        isSelected:
                            isAlgorithmPreselected[
                                AnalysisAlgorithmTypeEnum.KeyDrivers
                            ],
                    },
                ],
            });
        }
    }

    questionList.push(
        {
            id: AnalysisIds.OUTLIER_ID,
            title: getBusinessQuestionList()[0],
            type: A3AnalysisQuestionType.Outlier,
            shortName: translate('a3.shortNameOutlier'),
            question: translate('a3.questionOutlier'),
            image: 'resources/img/spotiq-img/Outlier.svg',
            isSelected:
                isQuestionPreselected[AnalysisClassEnum.OutlierDetection],
            algorithms: [
                {
                    name: translate('a3.OUTLIER_DETECTION'),
                    subparts: [
                        {
                            name: translate('a3.usingMedianZScores'),
                            type: A3AlgorithmType.OutlierDetectionMedianZScores,
                            isSelected:
                                isAlgorithmPreselected[
                                    AnalysisAlgorithmTypeEnum.MadMedian
                                ],
                        },
                        {
                            name: translate('a3.usingZScores'),
                            type: A3AlgorithmType.OutlierDetectionZScores,
                            isSelected:
                                isAlgorithmPreselected[
                                    AnalysisAlgorithmTypeEnum.StdevMean
                                ],
                        },
                    ],
                    isSelected:
                        isAlgorithmPreselected[
                            AnalysisAlgorithmTypeEnum.MadMedian
                        ] ||
                        isAlgorithmPreselected[
                            AnalysisAlgorithmTypeEnum.StdevMean
                        ],
                },
                {
                    name: translate('a3.SHESD_OUTLIER_DETECTION'),
                    type: A3AlgorithmType.OutlierDetectionSHESD,
                    isSelected:
                        isAlgorithmPreselected[AnalysisAlgorithmTypeEnum.Shesd],
                },
                {
                    name: translate('a3.LINEAR_REGRESSION_OUTLIER_DETECTION'),
                    type: A3AlgorithmType.OutlierDetectionLinearRegression,
                    isSelected:
                        isAlgorithmPreselected[
                            AnalysisAlgorithmTypeEnum.LinearRegression
                        ],
                },
            ],
        },
        {
            id: AnalysisIds.TREND_ID,
            title: getBusinessQuestionList()[1],
            type: A3AnalysisQuestionType.Trend,
            shortName: translate('a3.shortNameTrend'),
            question: translate('a3.questionTrend'),
            image: 'resources/img/spotiq-img/Trend.svg',
            isSelected: isQuestionPreselected[AnalysisClassEnum.TrendAnalysis],
            algorithms: [
                {
                    name: translate('a3.TREND_ANALYSIS'),
                    type: A3AlgorithmType.TrendAnalysis,
                    isSelected:
                        isAlgorithmPreselected[
                            AnalysisAlgorithmTypeEnum.TrendAnalysis
                        ],
                },
            ],
        },
        {
            id: AnalysisIds.CROSS_CORRELATION_ID,
            title: getBusinessQuestionList()[2],
            type: A3AnalysisQuestionType.CrossCorrelation,
            shortName: translate('a3.shortNameCrossCorrelations'),
            question: translate('a3.questionCrossCorrelation'),
            image: 'resources/img/spotiq-img/CrossCorrelation.svg',
            isSelected:
                isQuestionPreselected[AnalysisClassEnum.CrossCorrelation],
            algorithms: [
                {
                    name: translate('a3.CROSS_CORRELATION'),
                    type: A3AlgorithmType.CrossCorrelationAnalysis,
                    isSelected:
                        isAlgorithmPreselected[
                            AnalysisAlgorithmTypeEnum.CrossCorrelation
                        ],
                },
            ],
        },
    );

    // Select first analysis which is enabled only if none is already selected
    if (
        isSpotIQV2Enabled() &&
        !getSelectedQuestionFromQuestionListState(questionList)
    ) {
        const selectedQuestion = questionList.find(
            (question: A3BusinessQuestionConfig) => !question.disabledTooltip,
        );
        selectedQuestion.isSelected = true;
    }

    return questionList;
};
export const getQuestionListForChangeAnalysis = (): A3BusinessQuestionConfig[] => {
    const questionList = [
        {
            id: AnalysisIds.CHANGE_ANALYSIS_ID,
            title: getBusinessQuestionList()[3],
            type: A3AnalysisQuestionType.ChangeAnalysis,
            shortName: translate('a3.shortNameChange'),
            question: translate('a3.questionChange'),
            image: 'resources/img/spotiq-img/Change_analysis.svg',
            isSelected: true,
            algorithms: [
                {
                    name: translate('a3.CHANGE_ANALYSIS'),
                    type: A3AlgorithmType.ChangeAnalysis,
                    isSelected: true,
                },
            ],
        },
    ];
    return questionList;
};

export const getGeneralSettings = () => [
    { title: translate('a3.currentDataOnly'), isSelected: false },
    {
        title: translate('preferences.a3.excludeNullFromThisAnalysis'),
        isSelected: false,
    },
    {
        title: translate('preferences.a3.excludeZeroMeasureFromThisAnalysis'),
        isSelected: false,
    },
    {
        title: translate('preferences.a3.autotuneDateBoundaryFromThisAnalysis'),
        isSelected: true,
    },
];

export const getDefaultSelectedAlgorithms = (): Set<A3AlgorithmType> => {
    const selectedAlgorithms = new Set<A3AlgorithmType>();

    // Adding default algorithms to the set
    selectedAlgorithms.add(A3AlgorithmType.OutlierDetectionMedianZScores);
    selectedAlgorithms.add(A3AlgorithmType.OutlierDetectionLinearRegression);
    selectedAlgorithms.add(A3AlgorithmType.TrendAnalysis);
    selectedAlgorithms.add(A3AlgorithmType.CrossCorrelationAnalysis);
    if (isSpotIQV2Enabled()) selectedAlgorithms.add(A3AlgorithmType.Forecast);
    if (isWhatIfAnalysisEnabled())
        selectedAlgorithms.add(A3AlgorithmType.WhatIf);
    return selectedAlgorithms;
};

export const resetToDefaultAlgorithms = (
    prevQuestionListState: A3BusinessQuestionConfig[],
): A3BusinessQuestionConfig[] => {
    return prevQuestionListState.map((ques: any) => {
        if (!ques.isSelected) return ques;
        // Update the algorithm's subparts
        const updatedAlgorithms = ques.algorithms.map(
            (algo: any, index: number) => {
                if (algo.subparts) {
                    const anySubpartSelected = algo.subparts.some(
                        (subpart: any) =>
                            getDefaultSelectedAlgorithms().has(subpart.type),
                    );
                    return {
                        ...algo,
                        isSelected: anySubpartSelected,
                        subparts: (algo.subparts || []).map((subpart: any) => ({
                            ...subpart,
                            isSelected: getDefaultSelectedAlgorithms().has(
                                subpart.type,
                            ),
                        })),
                    };
                }
                // For the rest of the algorithms, update isSelected only
                return {
                    ...algo,
                    isSelected: getDefaultSelectedAlgorithms().has(algo.type),
                };
            },
        );
        return {
            ...ques,
            algorithms: updatedAlgorithms,
        };
    });
};

export const DEFAULT_MAX_MEASURES = 3;
export const DEFAULT_MAX_DRILL_ATTRIBUTES = 3;

export const getDefaultInsightCountSettings = (): any => {
    const maxMeasures =
        Number(
            isFeatureEnabled?.(FEATURE_FLAG_NAMES.SPOTIQ_MAX_MEASURES_LIMIT),
        ) || DEFAULT_MAX_MEASURES;
    const maxDrillAttributes =
        Number(
            isFeatureEnabled?.(
                FEATURE_FLAG_NAMES.SPOTIQ_MAX_DRILL_ATTRIBUTES_LIMIT,
            ),
        ) || DEFAULT_MAX_DRILL_ATTRIBUTES;

    return {
        maxMeasures,
        maxDrillAttributes,
        maxAnomalies: 20,
        maxAnomalyExplanationInsights: 20,
        maxShesdAnomalies: 3,
        // maxLrAnomalies: 5,
        maxTrends: 3,
        maxCrossCorrelationInsights: 3,
    };
};

export const insightCountSettingsForChangeAnalysis = {
    maxMeasures: 2,
    maxDrillAttributes: 5,
    maxAnomalies: 20,
    maxAnomalyExplanationInsights: 20,
    maxShesdAnomalies: 3,
    // maxLrAnomalies: 5,
    maxTrends: 3,
    maxCrossCorrelationInsights: 3,
};

export const DEFAULT_SELECTED_FORECASTED_DATAPOINTS = 10;
export const DEFAULT_SELECTED_VENDOR = Vendor.Thoughtspot;
export const FORECASTED_DATAPOINTS_GAP = 5;
export const parameterValues: SpotIQParams = {
    minRows: 5,
    multiplier: 2,
    pValueThreshold: 0.05,
    minRelativeDifference: 5,
    corrCoeff: 0.7,
    maxCorrCoeff: 0.98,
    maxLag: 50,
    maxDiffElements: 10,
    maxFraction: 0.5,
    minAbsChangeRatio: 0.1,
    minChangeRatio: 0.1,
    forecastedDatapoints: DEFAULT_SELECTED_FORECASTED_DATAPOINTS,
    vendor: DEFAULT_SELECTED_VENDOR,
};

// **************************** Customiser Utils  ****************************

export interface A3AnalysisSubmissionRequest {
    analysisType: A3AnalysisTypeEnum;
    // in case of data analysis type
    dataRows?: UserPointSelectionInput[] | null;
    // in case of viz analysis type, default: INCLUDE
    analyzeCurrentData?: CurrentDataOptionsEnum | null;
    selectedColumnsIds?: string[] | null;
    param?: AnalysisParam | null;
    title?: string | null;
    measureColumnId?: string | null;
}

export const baseSubmissionRequest: A3AnalysisSubmissionRequest = {
    analysisType: A3AnalysisTypeEnum.Visualization,
    // analyzeCurrentData: CurrentDataOptionsEnum.CurrentOnly, // confirm this
    selectedColumnsIds: [],
    title: 'Analysis!', // change to answer title
    param: {
        analysisDescriptor: [],
        excludeNull: false,
        maxInsightsOpts: {},
        excludeZeroMeasure: false,
        autotuneDateBoundary: false,
    },
};

/**
 * Populates the submission request for A3 analysis (all fields except analysisDescriptor)
 * @param analysisType
 * @param selectedColumnsIds
 * @param title
 * @param excludeNull
 * @param excludeZeroMeasure
 * @param autotuneDateBoundary
 * @param insightCountSettings
 */
export const initializeA3AnalysisSubmissionRequest = (
    analysisType: A3AnalysisTypeEnum,
    // analyzeCurrentData: CurrentDataOptionsEnum,  // confirm
    selectedColumnsIds: string[],
    title: string,
    restrictToCurrentResultSet: boolean,
    excludeNull: boolean,
    excludeZeroMeasure: boolean,
    autotuneDateBoundary: boolean,
    insightCount: MaxInsightOptions,
    dataRows: UserPointSelectionInput[],
    measureColumnId?: string,
) => {
    const analyzeCurrentData = restrictToCurrentResultSet
        ? CurrentDataOptionsEnum.CurrentOnly
        : CurrentDataOptionsEnum.Include;
    const param: AnalysisParam =
        analysisType === A3AnalysisTypeEnum.Visualization
            ? {
                  analysisDescriptor: [],
                  excludeNull,
                  maxInsightsOpts: { ...insightCount },
                  excludeZeroMeasure,
                  autotuneDateBoundary,
              }
            : {
                  analysisDescriptor: [],
              };
    const a3Request: A3AnalysisSubmissionRequest = {
        analysisType,
        analyzeCurrentData,
        selectedColumnsIds,
        title,
        param,
        dataRows,
        measureColumnId,
    };

    return a3Request;
};

/**
 * Checks if analysis is a change analysis.
 *
 * TODO: Decide if change analysis should be run on more than two points or not
 */
export const checkChangeAnalysis = (dataRows: UserPointSelectionInput[]) => {
    if (dataRows?.[0]) {
        return dataRows[0]?.dataValue.length >= 2;
    }

    return false;
};

export const getColumnData = (a3Columns: A3Column[]): columnDataType[] => {
    if (!a3Columns) {
        return [];
    }

    return a3Columns.map((a3Column: A3Column) => {
        return {
            column: a3Column.logicalColumn.displayName,
            columnId: a3Column.logicalColumn.guid,
            table: a3Column.ownerTable.displayName,
            isSuggested: a3Column.a3Suggested,
            isSelected: a3Column.a3Suggested,
            columnType: (a3Column.a3ColumnType as unknown) as ColumnType,
        };
    });
};

export const getSelectedAlgorithmsFromQuestionListState = (
    questionListState: A3BusinessQuestionConfig[],
): Set<A3AlgorithmType> => {
    const selectedAlgorithms = new Set<A3AlgorithmType>();
    questionListState.forEach(a3BusinessQuestionConfig => {
        if (!a3BusinessQuestionConfig.isSelected) {
            return;
        }

        a3BusinessQuestionConfig.algorithms.forEach(algorithm => {
            if (!algorithm.isSelected) {
                return;
            }

            if (algorithm.type) {
                selectedAlgorithms.add(algorithm.type);
            } else {
                algorithm.subparts.forEach(algoSubpart => {
                    if (algoSubpart.isSelected) {
                        selectedAlgorithms.add(algoSubpart.type);
                    }
                });
            }
        });
    });
    return selectedAlgorithms;
};

export const getToolTipContent = (
    columnTitle: string,
    heading: string,
): string => {
    return `${translate('filterTooltip.Content.FILTER', [
        columnTitle,
    ])}\n${translate('filterTooltip.Content.DATA_SOURCE', [heading])}`;
};

// Helper to extract comparison points
const getComparisonPoint = (
    contextMenuPoints: ContextMenuInputPoints,
    pointIndex: number,
) => {
    return _.get(
        contextMenuPoints,
        `selectedPoints.${pointIndex}.selectedAttributes.0.value`,
    );
};

// Helper to find the index of comparison points in the x-axis data
export const getComparisonPointIndex = (
    data: any[],
    point: any,
    axisConfig: AnswerColumn,
) => {
    if (_.isArray(data)) {
        return data.findIndex((dataPoint: any) => {
            if (hasCustomCalendar(axisConfig)) {
                // If custom calendar is applied on any column, the datapoint will be in an object format like:
                // {
                //     "v": {
                //         "s": 694252800
                //          //optional
                //         "e":694252600
                //     }
                // }
                // we need compare the equality of the points accordingly
                const startEpochDataPoint = getStartEpoch(dataPoint);
                const endEpochDataPoint = getEndEpoch(dataPoint);
                const startEpochPoint = getStartEpoch(point);
                const endEpochPoint = getEndEpoch(point);

                const isValidEpochs =
                    !_.isNull(startEpochDataPoint) &&
                    !_.isNull(endEpochDataPoint) &&
                    !_.isNull(startEpochPoint) &&
                    !_.isNull(endEpochPoint);

                const isValidStartEpoch =
                    !_.isNull(startEpochDataPoint) &&
                    !_.isNull(startEpochPoint);

                if (isValidEpochs) {
                    return (
                        getStartEpoch(dataPoint) === getStartEpoch(point) &&
                        getEndEpoch(dataPoint) === getEndEpoch(point)
                    );
                }
                if (isValidStartEpoch) {
                    return getStartEpoch(dataPoint) === getStartEpoch(point);
                }
                return false;
            }
            // Non-object values (like string, number, boolean, etc.)
            // should be compared in this way
            if (!_.isObject(dataPoint) && !_.isObject(point)) {
                return String(dataPoint) === String(point);
            }
            // Fallback for handling possible future scenarios
            return JSON.stringify(dataPoint) === JSON.stringify(point);
        });
    }
    return -1;
};

// Helper to get columnData from chartViz
const getColumnDataFromChartViz = (chartViz: ChartViz, columnId: string) => {
    return chartViz?.data?.[0]?.columnDataLite?.find(
        (columnData: any) => columnData.columnId === columnId,
    );
};

/**
 * Generates new data rows for an Iterative Change Analysis (CCA) process.
 *
 * This function is used to create data rows required for performing an Iterative CCA
 * analysis based on the provided chart visualization and context menu points.
 * @param {ChartViz} chartViz - The chart visualization data containing information
 * about the data, configuration, and axis settings.
 * @param {ContextMenuInputPoints} contextMenuPoints - The context menu points that
 * define comparison attributes and values for the CCA process.
 *
 * @returns {UserPointSelectionInput[] | []} An array of new data rows for analysis,
 * function will return an empty array ([]) in case no measure values can be found using original points.
 */

export const generateNewDataRows = (
    chartViz: ChartViz,
    contextMenuPoints: ContextMenuInputPoints,
): UserPointSelectionInput[] | [] => {
    if (!chartViz?.data?.[0]) return [];

    const axisConfig = chartViz?.config?.axisConfig?.[0];
    const xAxisColumnId = axisConfig?.x?.[0]?.id;
    const yAxisColumnId = axisConfig?.y?.[0]?.id;

    const finalComparisonPoint = getComparisonPoint(contextMenuPoints, 0);
    const initialComparisonPoint = getComparisonPoint(contextMenuPoints, 1);

    const xAxisData = getColumnDataFromChartViz(chartViz, xAxisColumnId);
    const yAxisData = getColumnDataFromChartViz(chartViz, yAxisColumnId);

    if (!xAxisData || !yAxisData || !xAxisData?.dataValue) return [];

    const initialComparisonPointIndex = getComparisonPointIndex(
        xAxisData.dataValue,
        initialComparisonPoint,
        axisConfig?.x[0],
    );
    const finalComparisonPointIndex = getComparisonPointIndex(
        xAxisData.dataValue,
        finalComparisonPoint,
        axisConfig?.x[0],
    );

    if (
        initialComparisonPointIndex === -1 ||
        finalComparisonPointIndex === -1
    ) {
        return [];
    }

    const initialComparisonPointMeasureValue =
        yAxisData.dataValue[initialComparisonPointIndex];
    const finalComparisonPointMeasureValue =
        yAxisData.dataValue[finalComparisonPointIndex];

    return [
        {
            columnId: yAxisColumnId,
            dataValue: [
                {
                    value: String(finalComparisonPointMeasureValue),
                },
                {
                    value: String(initialComparisonPointMeasureValue),
                },
            ],
        },
        ...getUserSelectionPoints(contextMenuPoints),
    ];
};

export const columnTypes = [ColumnType.Measure, ColumnType.Attribute];

export const getAccordionHeader = (columnType: ColumnType) => {
    if (columnType === ColumnType.Attribute)
        return translate('dataPanel.attributes');
    if (columnType === ColumnType.Measure)
        return translate('dataPanel.measures');
    return '';
};
